import { ATTRIBUTION_COOKIE } from "../constants/AuthConstant";
import { getCookie } from "./cookieHandler";

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
  "li_fat_id",
  "rdt_cid",
];

const FIFTEEN_DAYS_MS = 15 * 24 * 60 * 60 * 1000;

const writeAttributionCookie = (value) => {
  const expires = new Date(Date.now() + FIFTEEN_DAYS_MS).toUTCString();
  const domainString = window.location.host.includes("localhost")
    ? ""
    : `Domain=${window.location.host};`;
  const encoded = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${ATTRIBUTION_COOKIE}=${encoded}; expires=${expires}; ${domainString} SameSite=Lax; Secure; path=/`;
};

// First-touch attribution: only write the cookie if it doesn't already exist
// and the current URL carries at least one tracked param. Subsequent visits
// with new UTMs do not overwrite — the original campaign wins.
export const captureAttributionFromUrl = () => {
  if (getCookie(ATTRIBUTION_COOKIE)) return;

  const params = new URLSearchParams(window.location.search);
  const hasAny = TRACKED_PARAMS.some((key) => params.has(key));
  if (!hasAny) return;

  const attribution = TRACKED_PARAMS.reduce((acc, key) => {
    acc[key] = params.get(key) || null;
    return acc;
  }, {});
  attribution.landing_page = window.location.pathname || null;
  attribution.referrer = document.referrer || null;
  attribution.captured_at = new Date().toISOString();

  writeAttributionCookie(attribution);
};

export const getStoredAttribution = () => {
  const raw = getCookie(ATTRIBUTION_COOKIE);
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

// --- client_register_url -----------------------------------------------------
//
// The complete frontend URL the visitor was on when their account was created.
// The backend stores it on the user and, once, on the company (it is never
// overwritten by later signups on the same domain), and falls back to the
// Referer header when this field is absent — so sending it is an improvement,
// not a requirement.

const CLIENT_REGISTER_URL_KEY = "client_register_url";

// Query params that must never reach the backend: OAuth material and anything
// credential-like that can appear on an auth URL.
const SENSITIVE_PARAMS = [
  "code",
  "credential",
  "state",
  "id_token",
  "access_token",
  "token",
  "otp_code",
  "password",
  "secret",
];

// Matches the backend cap (AttributionWriter::MAX_URL_LENGTH); a longer value
// would be dropped there anyway.
const MAX_URL_LENGTH = 2048;

const sanitizeUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    SENSITIVE_PARAMS.forEach((param) => url.searchParams.delete(param));
    const cleaned = url.toString();
    return cleaned.length > MAX_URL_LENGTH ? null : cleaned;
  } catch {
    return null;
  }
};

/** The current page, cleaned. Null rather than a guess when it cannot be read. */
export const getClientRegisterUrl = () => {
  if (typeof window === "undefined") return null;
  return sanitizeUrl(window.location.href);
};

/**
 * Snapshot the current page before handing off to an SSO provider.
 *
 * Without this the exchange would report the /auth/{provider}/callback URL,
 * which is both useless (it is not where the user signed up) and carries the
 * OAuth code. Kept in sessionStorage rather than the OAuth `state` param so the
 * URL is never handed to Google or Microsoft.
 */
export const rememberClientRegisterUrl = () => {
  const url = getClientRegisterUrl();
  if (!url) return;
  try {
    window.sessionStorage.setItem(CLIENT_REGISTER_URL_KEY, url);
  } catch {
    // Private mode / storage disabled — the backend's Referer fallback covers it.
  }
};

const clearClientRegisterUrlCookie = () => {
  const domainString = window.location.host.includes("localhost")
    ? ""
    : "Domain=.teamsunday.com;";
  document.cookie = `${CLIENT_REGISTER_URL_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${domainString} path=/`;
};

/**
 * The remembered pre-redirect page, consumed on read.
 *
 * Two sources, because an SSO hop can start in either place:
 *   1. sessionStorage — the user clicked SSO here, in the app.
 *   2. the ".teamsunday.com" cookie — the user clicked SSO on the marketing
 *      site, which cannot reach this origin's sessionStorage. Same
 *      cross-subdomain trick the attribution cookie already relies on.
 *
 * Falls back to the current page, which is correct for Google One Tap (no
 * redirect happens there).
 */
export const takeStoredClientRegisterUrl = () => {
  try {
    const stored = window.sessionStorage.getItem(CLIENT_REGISTER_URL_KEY);
    if (stored) {
      window.sessionStorage.removeItem(CLIENT_REGISTER_URL_KEY);
      const cleaned = sanitizeUrl(stored);
      if (cleaned) return cleaned;
    }
  } catch {
    // fall through to the cookie
  }

  const fromCookie = getCookie(CLIENT_REGISTER_URL_KEY);
  if (fromCookie) {
    clearClientRegisterUrlCookie();
    const cleaned = sanitizeUrl(decodeURIComponent(fromCookie));
    if (cleaned) return cleaned;
  }

  return getClientRegisterUrl();
};
